import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSignOutAlt, FaNewspaper, FaFileAlt, FaTrash } from 'react-icons/fa';
import { apiFetch } from '../api/httpClient';
import { API_BASE_URL } from '../config/config';
import './AdminPanel.css';

const AdminPanel = () => {
  const navigate = useNavigate();
  const [option, setOption] = useState('main');
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const [noticias, setNoticias] = useState([]);
  const [loadingNoticias, setLoadingNoticias] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [noticiaToDelete, setNoticiaToDelete] = useState(null);

  const [newsTitle, setNewsTitle] = useState('');
  const [newsContent, setNewsContent] = useState('');
  const [newsImage, setNewsImage] = useState(null);
  const [actaFile, setActaFile] = useState(null);
  const [videoFecha, setVideoFecha] = useState('');
  const [videoUrl, setVideoUrl] = useState('');

  const handleLogout = async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } catch (err) {
      console.error('❌ Error cerrando sesión manual:', err);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('usuario');
      navigate('/login');
    }
  };

  useEffect(() => {
    if (option === 'noticia') {
      cargarNoticias();
    }
  }, [option]);

  const cargarNoticias = async () => {
    setLoadingNoticias(true);
    try {
      const data = await apiFetch('/noticias/ultimas');
      setNoticias(data || []);
    } catch (err) {
      console.error('Error cargando noticias:', err);
      setError('Error al cargar las noticias');
    } finally {
      setLoadingNoticias(false);
    }
  };

  const handleEliminarNoticiaClick = (id, titulo) => {
    setNoticiaToDelete({ id, titulo });
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!noticiaToDelete) return;

    try {
      await apiFetch(`/noticias/${noticiaToDelete.id}`, { method: 'DELETE' });
      setMensaje('✅ Noticia eliminada correctamente');
      setError(''); // Limpiar errores previos
      setShowConfirmModal(false);
      setNoticiaToDelete(null);
      cargarNoticias(); // Recargar la lista
    } catch (err) {
      console.error('Error al eliminar la noticia:', err);
      setError('❌ Error al eliminar la noticia. Por favor, intenta de nuevo.');
      setMensaje(''); // Limpiar mensajes previos
      setShowConfirmModal(false);
      setNoticiaToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setNoticiaToDelete(null);
  };

  const handleOptionBack = () => {
    setOption('main');
    setMensaje('');
    setError('');
  };

  const handleBack = () => navigate(-1);

  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    setMensaje('');
    setError('');

    const formData = new FormData();
    formData.append('titulo', newsTitle);
    formData.append('contenido', newsContent);
    formData.append('imagen', newsImage);

    try {
      await apiFetch('/noticias', {
        method: 'POST',
        body: formData,
      });

      setMensaje('📰 Noticia subida correctamente');
      setNewsTitle('');
      setNewsContent('');
      setNewsImage(null);
      setOption('main');
      cargarNoticias(); // Recargar la lista después de crear
    } catch (err) {
      console.error('Error al subir la noticia:', err);
      setError('❌ Error al subir la noticia');
    }
  };

  const handleActaSubmit = async (e) => {
    e.preventDefault();
    setMensaje('');
    setError('');

    const formData = new FormData();
    formData.append('archivo', actaFile);

    try {
      await apiFetch('/actas', {
        method: 'POST',
        body: formData,
      });

      setMensaje('📄 Acta subida correctamente');
      setActaFile(null);
      setOption('main');
    } catch (err) {
      console.error('Error al subir el acta:', err);
      setError('❌ No se pudo subir el acta');
    }
  };

  const formatearTituloVideo = (fechaStr) => {
    const fecha = new Date(fechaStr);
    const opciones = { day: 'numeric', month: 'long', year: 'numeric' };
    return `Video pleno ${fecha.toLocaleDateString('es-ES', opciones)}`;
  };

  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    setMensaje('');
    setError('');

    if (!videoFecha || !videoUrl) {
      setError('❌ Debes ingresar fecha y URL del video');
      return;
    }

    const titulo = formatearTituloVideo(videoFecha);
    console.log('Enviando:', {
      titulo,
      fecha: videoFecha,
      url: videoUrl
    });

    try {
      await apiFetch('/actas-video', {
        method: 'POST',
        body: JSON.stringify({ titulo, fecha: videoFecha, url: videoUrl }),
      });


      setMensaje('🎥 Video de pleno subido correctamente');
      setVideoFecha('');
      setVideoUrl('');
      setOption('main');
    } catch (err) {
      console.error('❌ Error al subir el video:', err);
      setError(`❌ Error del servidor: ${err.message}`);
    }
  };

  return (
    <div className="admin-panel-container">
      {/* Modal de Confirmación */}
      {showConfirmModal && noticiaToDelete && (
        <div className="confirm-modal-overlay" onClick={handleCancelDelete}>
          <div className="confirm-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <h3>¿Eliminar Noticia?</h3>
            </div>
            <div className="confirm-modal-body">
              <p>Estás a punto de eliminar la siguiente noticia:</p>
              <div className="confirm-modal-noticia-titulo">
                {noticiaToDelete.titulo}
              </div>
              <p className="confirm-modal-warning">
                Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="confirm-modal-footer">
              <button 
                className="confirm-modal-button cancel-button"
                onClick={handleCancelDelete}
              >
                Cancelar
              </button>
              <button 
                className="confirm-modal-button delete-button"
                onClick={handleConfirmDelete}
              >
                <FaTrash /> Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      <header className="admin-header">
        <button className="icon-button" onClick={handleBack} title="Volver">
          <FaArrowLeft />
        </button>
        <button className="icon-button" onClick={handleLogout} title="Cerrar sesión">
          <FaSignOutAlt />
        </button>
      </header>

      <div className="admin-title">
        <h1>Panel de Gestión de Contenido</h1>
        <p>Elige una opción para subir una noticia, acta o video de pleno.</p>
      </div>

      {mensaje && <p className="mensaje-exito">{mensaje}</p>}
      {error && <p className="mensaje-error">{error}</p>}

      {option === 'main' && (
        <div className="admin-options">
          <button className="option-button" onClick={() => setOption('noticia')}>
            <FaNewspaper className="option-icon" />
            <span>Subir Noticia</span>
          </button>
          <button className="option-button" onClick={() => setOption('acta')}>
            <FaFileAlt className="option-icon" />
            <span>Subir Acta de Pleno</span>
          </button>
          <button className="option-button" onClick={() => setOption('video')}>
            <FaFileAlt className="option-icon" />
            <span>Subir Video de Pleno</span>
          </button>
        </div>
      )}

      {option === 'noticia' && (
        <>
          <div className="form-container">
            <header className="form-header">
              <button className="icon-button" onClick={handleOptionBack}>
                <FaArrowLeft />
              </button>
              <h2>Subir Noticia</h2>
            </header>
            <form onSubmit={handleNewsSubmit}>
              <label>Título:</label>
              <input type="text" value={newsTitle} onChange={(e) => setNewsTitle(e.target.value)} required />
              <label>Seleccionar Imagen:</label>
              <input type="file" accept="image/*" onChange={(e) => setNewsImage(e.target.files[0])} required />
              <label>Texto completo de la Noticia:</label>
              <textarea value={newsContent} onChange={(e) => setNewsContent(e.target.value)} required />
              <button type="submit" className="submit-button">Enviar Noticia</button>
            </form>
          </div>

          <div className="admin-noticias-section">
            <h3 className="admin-noticias-title">Últimas Noticias ({noticias.length})</h3>
            {loadingNoticias ? (
              <p>Cargando noticias...</p>
            ) : noticias.length === 0 ? (
              <p className="no-noticias">No hay noticias disponibles</p>
            ) : (
              <div className="admin-noticias-grid">
                {noticias.map((noticia) => {
                  const imagenUrl = noticia.imagenPath?.startsWith('http') 
                    ? noticia.imagenPath 
                    : noticia.imagenPath?.startsWith('/uploads')
                      ? `${API_BASE_URL.replace('/api', '')}${noticia.imagenPath}`
                      : noticia.imagenPath;
                  
                  return (
                    <div key={noticia.id} className="admin-noticia-item">
                      {imagenUrl && (
                        <div className="admin-noticia-image">
                          <img 
                            src={imagenUrl}
                            alt={noticia.titulo}
                            onError={(e) => {
                              console.error('Error cargando imagen:', imagenUrl);
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <div className="admin-noticia-content">
                        <h4 className="admin-noticia-titulo">{noticia.titulo}</h4>
                        <p className="admin-noticia-fecha">
                          {noticia.fechaCreacion 
                            ? new Date(noticia.fechaCreacion).toLocaleDateString('es-ES', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })
                            : 'Sin fecha'}
                        </p>
                        <button
                          className="admin-delete-button"
                          onClick={() => handleEliminarNoticiaClick(noticia.id, noticia.titulo)}
                          title="Eliminar noticia"
                        >
                          <FaTrash /> Eliminar
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {option === 'acta' && (
        <div className="form-container">
          <header className="form-header">
            <button className="icon-button" onClick={handleOptionBack}>
              <FaArrowLeft />
            </button>
            <h2>Subir Acta de Pleno</h2>
          </header>
          <form onSubmit={handleActaSubmit}>
            <label>Seleccionar Documento (PDF):</label>
            <input type="file" accept="application/pdf" onChange={(e) => setActaFile(e.target.files[0])} required />
            <button type="submit" className="submit-button">Enviar Acta</button>
          </form>
        </div>
      )}

      {option === 'video' && (
        <div className="form-container">
          <header className="form-header">
            <button className="icon-button" onClick={handleOptionBack}>
              <FaArrowLeft />
            </button>
            <h2>Subir Video de Pleno</h2>
          </header>
          <form onSubmit={handleVideoSubmit}>
            <label>Fecha del pleno:</label>
            <input type="date" value={videoFecha} onChange={(e) => setVideoFecha(e.target.value)} required />
            <label>URL del video:</label>
            <input type="url" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} required />
            <button type="submit" className="submit-button">Enviar Video</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
